import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import moment from "moment";

import {
  LOGOUT_SUCCESS,
  REFRESH_LAST_ACTION_PERFORMED_AT,
  setOnlineRequiredError,
  getIsOfflineMode,
} from "data-handler/ducks/auth";
import { Store } from "redux";

type RetryableConfig = AxiosRequestConfig & { csrfRetried?: boolean };

type ResponseError = {
  config?: RetryableConfig;
  response?: AxiosResponse;
};

const CSRF_COOKIE = "csrftoken";
const CSRF_HEADER = "X-CSRFToken";
const SAFE_METHODS = ["get", "head", "options", "trace"];

// Consecutive-failure backoff: the first few failures are free (a single page
// load fires several requests in parallel), after which the delay applied
// before rejecting doubles per failure. Response-driven retry loops issue
// their next request only once the previous one settles, so delaying the
// rejection caps the request rate of any such loop, whatever component
// causes it.
const BACKOFF_FREE_FAILURES = 3;
const BACKOFF_BASE_MS = 500;
const BACKOFF_MAX_MS = 30000;

let consecutiveFailureCount = 0;
let sessionTerminated = false;

export const readCookie = (name: string): string | undefined =>
  document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];

// The API sets the CSRF cookie on its own host, so on deployed environments
// this app - served from the parent domain - cannot read it and asks the API
// for the value instead. Locally the two share a host and the cookie is right
// there, hence the cookie-first lookup.
let csrfToken: string | undefined;
let pendingCsrfFetch: Promise<string | undefined> | undefined;
let csrfCookieRejected = false;

const csrfUrl = () => `${process.env.REACT_APP_API_URL}/csrf/`;

const isCsrfRequest = (config?: AxiosRequestConfig) =>
  config?.url === csrfUrl();

const fetchCsrfToken = async (): Promise<string | undefined> => {
  const response = await axios.get(csrfUrl());
  return response?.data?.csrf_token;
};

const forgetCsrfToken = () => {
  csrfToken = undefined;
  csrfCookieRejected = true;
};

const getCsrfToken = async (offline: boolean): Promise<string | undefined> => {
  const fromCookie = readCookie(CSRF_COOKIE);
  // the cookie is authoritative until the server turns it down - after that
  // re-sending it just buys another 403
  if (fromCookie && !csrfCookieRejected) {
    return fromCookie;
  }
  if (csrfToken) {
    return csrfToken;
  }
  // offline writes have nowhere to go anyway, don't put a round-trip in front
  if (offline) {
    return undefined;
  }
  // one fetch serves every write queued while it is in flight
  if (!pendingCsrfFetch) {
    pendingCsrfFetch = fetchCsrfToken()
      .catch(() => undefined)
      .then((token) => {
        csrfToken = token;
        pendingCsrfFetch = undefined;
        return token;
      });
  }
  return pendingCsrfFetch;
};

const isCsrfFailure = (error: ResponseError) => {
  const data = error.response?.data;
  // deployed settings may or may not wrap DRF errors under `errors`
  const detail = data?.detail ?? data?.errors?.detail;
  return (
    error.response?.status === 403 &&
    typeof detail === "string" &&
    detail.startsWith("CSRF Failed")
  );
};

/**
 * Ends the session client-side so PrivateRoute redirects to the login page.
 * Dispatches LOGOUT_SUCCESS directly instead of LOGOUT: the LOGOUT saga POSTs
 * /logout/ on a session that has already gone, which can only fail again and
 * would neither clear the state nor redirect.
 */
const terminateSession = (store: Store) => {
  if (sessionTerminated) {
    return;
  }
  sessionTerminated = true;
  store.dispatch({ type: LOGOUT_SUCCESS });
};

const isBackoffStatus = (status: number) =>
  status === 401 || status === 403 || status === 429 || status >= 500;

const backoffDelayMs = () => {
  const failuresOverAllowance = consecutiveFailureCount - BACKOFF_FREE_FAILURES;
  if (failuresOverAllowance <= 0) {
    return 0;
  }
  return Math.min(
    BACKOFF_BASE_MS * 2 ** (failuresOverAllowance - 1),
    BACKOFF_MAX_MS
  );
};

const rejectWithBackoff = async (error: ResponseError) => {
  const status = error.response?.status;
  if (status !== undefined && isBackoffStatus(status)) {
    consecutiveFailureCount += 1;
    const delay = backoffDelayMs();
    if (delay > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  return Promise.reject(error);
};

export default {
  setupInterceptors: (store: Store) => {
    // The API credential is an httpOnly session cookie the browser cannot
    // read, so calls carry credentials instead of a Bearer header.
    axios.defaults.withCredentials = true;

    axios.interceptors.request.use(
      async (config: AxiosRequestConfig) => {
        const method = (config.method || "get").toLowerCase();
        if (!SAFE_METHODS.includes(method)) {
          const token = await getCsrfToken(getIsOfflineMode(store.getState()));
          if (token) {
            config.headers[CSRF_HEADER] = token;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    axios.interceptors.response.use(
      async (response: AxiosResponse) => {
        // housekeeping, not user activity - counting it would let a csrf
        // rejection loop keep resetting the backoff
        if (isCsrfRequest(response.config)) {
          return response;
        }
        consecutiveFailureCount = 0;
        sessionTerminated = false;
        // whatever we sent was accepted, so the cookie is trustworthy again
        csrfCookieRejected = false;
        store.dispatch({
          type: REFRESH_LAST_ACTION_PERFORMED_AT,
          data: moment().toString(),
        });
        return response;
      },
      async (error: ResponseError) => {
        const state = store.getState();

        if (getIsOfflineMode(state)) {
          return Promise.reject(error);
        }

        // A token the server has since rotated: drop it and let the request
        // interceptor fetch a fresh one on the way back through.
        if (isCsrfFailure(error) && error.config && !error.config.csrfRetried) {
          forgetCsrfToken();
          error.config.csrfRetried = true;
          return axios(error.config);
        }

        // Refreshing is the backend's job now - it holds the CIAM tokens and
        // renews them behind the session cookie. A 401 therefore means the
        // session is genuinely over, not that a token needs trading in.
        if (error.response?.status === 401) {
          // the next session gets its own secret, ours is worthless now
          forgetCsrfToken();
          terminateSession(store);
          store.dispatch(setOnlineRequiredError());
        }

        return rejectWithBackoff(error);
      }
    );
  },
};
